import type { StyleRule } from '@archipro-design/aria';
import {
    pxArrayToRem,
    pxToRem,
    HeaderClassName,
    labelClassName,
    buttonClassName,
    avatarClassName,
} from '@archipro-design/aria';

export const ArticleDetailsContainer: StyleRule = ({ theme }) => ({
    textAlign: 'center',
    margin: 'auto',
    padding: pxArrayToRem([0, 18]),
    marginBottom: pxToRem(18),

    [theme.screen.laptop]: {
        marginBottom: 0,
        padding: pxArrayToRem([75, 50, 60, 50]),
        maxWidth: pxToRem(1420),
    },
});

export const ArticleIntroContainer: StyleRule = ({ theme }) => ({
    textAlign: 'center',
    margin: 'auto',
    padding: pxArrayToRem([55, 18, 0]),
    marginBottom: pxToRem(55),

    [theme.screen.laptop]: {
        marginBottom: 0,
        padding: pxArrayToRem([80, 50, 0, 50]),
        maxWidth: pxToRem(1420),
    },
});

export const ArticleHeading: StyleRule = ({ theme }) => ({
    [`&.${HeaderClassName}`]: {
        ...theme.siteVariables.textStyles.Article.Roman55,
    },
    lineHeight: theme.siteVariables.lineHeightSmallest,
    marginTop: pxToRem(22),
    marginBottom: pxToRem(21),
    color: theme.siteVariables.colorScheme.default?.foreground2,

    [theme.screen.laptop]: {
        marginTop: '0.67em',
        marginBottom: theme.siteVariables.spaces['space-36'],
        lineHeight: '110%',
        color: theme.siteVariables.colorScheme.default?.foreground,
    },
});

export const ArticleAuthor: StyleRule = ({ theme, isApStaff }) => ({
    margin: pxArrayToRem([0, 0, 6, 0]),
    textTransform: 'uppercase',

    '& p': {
        margin: 0,
        ...theme.siteVariables.textStyles.Text.Medium65,
        fontSize: theme.siteVariables.fontSizes.caption05,
        lineHeight: 1.25,
        letterSpacing: '0.08em',
        color: theme.siteVariables.colors.charcoal['350'],
    },

    [`& .${buttonClassName}`]: {
        marginLeft: pxToRem(5),
        ':hover': {
            cursor: isApStaff ? 'default' : 'pointer',
            textDecoration: isApStaff ? 'none' : 'underline',
        },
    },

    [theme.screen.laptop]: {
        justifyContent: 'flex-start',
        textTransform: 'unset',
        '& p': {
            ...theme.siteVariables.textStyles.Text.Roman55,
            fontSize: theme.siteVariables.fontSizes.label03,
            lineHeight: 1.32,
            letterSpacing: theme.siteVariables.letterSpacingSmall,
            color: theme.siteVariables.colors.charcoal['000'],
        },

        '& > p:first-of-type': {
            color: theme.siteVariables.colors.grey['400'],
        },
    },
});

export const IntroTextDesktop: StyleRule = ({ theme }) => ({
    ...theme.siteVariables.getTextStyle('Medium65', 'Article'),
});

export const IntroTextMobile: StyleRule = ({ theme }) => ({
    ...theme.siteVariables.getTextStyle('Medium65', 'Article'),
});

export const AvatarAndAuthor: StyleRule = ({ theme, isApStaff }) => ({
    [theme.screen.laptop]: {
        padding: pxArrayToRem([21, 28]),
    },
    [`& .${avatarClassName}`]: {
        marginRight: theme.siteVariables.spaces['space-20'],
        '& img': {
            objectFit: isApStaff ? 'cover' : 'contain',
            padding: isApStaff ? 0 : pxToRem(2),
        },
    },
});

export const ExtraInfo: StyleRule = ({ theme }) => ({
    '& p': {
        margin: 0,
        ...theme.siteVariables.textStyles.Text.Roman55,
        textTransform: 'uppercase',
        fontSize: theme.siteVariables.fontSizes.caption05,
        lineHeight: 1.25,
        letterSpacing: '0.08em',
        color: theme.siteVariables.colors.charcoal['350'],
    },
    justifyContent: 'center',
    [theme.screen.laptop]: {
        justifyContent: 'flex-start',
        '& p': {
            color: theme.siteVariables.colors.grey['400'],
        },
    },
});

export const SaveToDesignBoardWrapper: StyleRule = ({ theme }) => ({
    margin: pxArrayToRem([24, 0, 0, 0]),
    justifyContent: 'center',
    [`& .${buttonClassName}`]: {
        [`& > span `]: {
            width: pxToRem(18),
            padding: 0,
        },
        [`& .${labelClassName}`]: {
            textDecoration: 'underline',
            color: theme.siteVariables.colors.charcoal[250],
        },
    },

    // CHange font only for mobile
    [theme.screen.max.laptop]: {
        marginRight: pxToRem(15),
        [`& .${buttonClassName}`]: {
            [`& .${labelClassName}`]: {
                ...theme.siteVariables.textStyles.Heading.Roman55,
                textTransform: 'uppercase',
            },
        },
    },

    [theme.screen.laptop]: {
        margin: pxArrayToRem([8, 0, 0, 0]),
        justifyContent: 'flex-start',
        [`& .${buttonClassName}`]: {
            marginRight: pxToRem(25),
            [`& > span `]: {
                width: pxToRem(16),
                padding: 0,
            },
        },
    },
});

export const ShareAndSaveContainer: StyleRule = ({ theme }) => ({
    justifyContent: 'center',
    alignItems: 'end',
    [theme.screen.tablet]: {
        alignItems: 'baseline',
        justifyContent: 'flex-start',
    },
});

export const LinkButton: StyleRule = () => ({
    textDecoration: 'underline',
    '& > span': {
        marginRight: pxToRem(12),
    },
});

export const ArticlePageContainer: StyleRule<{
    isDesktop: boolean;
}> = ({ theme, isDesktop }) => ({
    overflowX: `${isDesktop ? 'auto' : 'hidden'}`,
    backgroundColor: theme.siteVariables.bodyBackground,
});
